import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Sparkles, ArrowLeft, Plus, Trash2, ExternalLink, Loader2 } from "lucide-react";

const OCCASIONS = ["Wedding", "Party", "Office", "Date Night", "Festival", "Beach", "Formal Event", "Casual Outing"];

const blank = {
  amazon_url: "",
  occasion: "Wedding",
  gender: "female",
  tags: "",
  title: "",
  image_url: "",
  price: "",
  brand: "",
};

export default function AdminCatalog() {
  const { user } = useAuth();
  const isAdminUser = !!user?.is_admin;
  const [secret, setSecret] = useState(() => localStorage.getItem("admin_secret") || "");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const adminHeaders = secret && !isAdminUser ? { "X-Admin-Secret": secret } : undefined;

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/catalog", {
        headers: secret && !isAdminUser ? { "X-Admin-Secret": secret } : undefined,
      });
      setItems(data || []);
      setAuthed(true);
      if (secret && !isAdminUser) localStorage.setItem("admin_secret", secret);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAuthed(false);
        toast.error("Invalid admin secret");
      } else {
        toast.error("Could not load catalog");
      }
    } finally {
      setLoadingList(false);
    }
  }, [secret, isAdminUser]);

  useEffect(() => { if (secret || isAdminUser) loadList(); }, [secret, isAdminUser, loadList]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        amazon_url: form.amazon_url,
        occasion: form.occasion,
        gender: form.gender,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        title: form.title || undefined,
        image_url: form.image_url || undefined,
        price: form.price || undefined,
        brand: form.brand || undefined,
      };
      await api.post("/admin/catalog", payload, { headers: adminHeaders });
      toast.success("Product added");
      setForm(blank);
      loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this product?")) return;
    try {
      await api.delete(`/admin/catalog/${id}`, { headers: adminHeaders });
      setItems(items.filter((p) => p.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2] flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12" data-testid="admin-catalog-logo">
            <Sparkles className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />
            <span className="font-serif text-2xl">StyleMe · Admin</span>
          </Link>
          <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Restricted</span>
          <h1 className="font-serif text-4xl mt-3">Catalog management.</h1>
          <p className="mt-4 text-[#A8A6A1] text-sm">Enter the admin secret to manage curated Amazon products.</p>
          <input
            type="password"
            placeholder="Admin secret"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadList()}
            data-testid="admin-secret-input"
            className="mt-8 w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] text-lg font-light"
          />
          <button
            onClick={loadList}
            data-testid="admin-secret-submit"
            className="mt-8 w-full bg-[#E3C2B0] text-[#0A0908] py-4 rounded-sm hover:bg-[#F0D4C5] transition-colors text-xs uppercase tracking-[0.3em]"
          >
            Unlock
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-catalog-back">
            <Sparkles className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />
            <span className="font-serif text-2xl">StyleMe · Admin</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2]">
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to site
          </Link>
        </div>

        <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Amazon Catalog</span>
        <h1 className="font-serif text-5xl mt-3 font-light">Curate the shoppable looks.</h1>
        <p className="mt-4 text-[#A8A6A1] text-sm max-w-2xl leading-relaxed">
          Hand-pick Amazon products to surface alongside AI styling recommendations. Affiliate tag <span className="text-[#E3C2B0]">stylenme26-21</span> is auto-appended to every link.
          The scraper is best-effort — for reliable results, paste <em>title</em> and <em>image_url</em> manually (right-click Amazon&apos;s product photo → &quot;Copy image address&quot;).
        </p>

        {/* Form */}
        <form onSubmit={submit} data-testid="admin-catalog-form" className="mt-12 grid md:grid-cols-2 gap-6 p-8 rounded-sm border border-white/10 bg-white/[0.02]">
          <Field label="Amazon URL *" type="url" required value={form.amazon_url} onChange={(v) => setForm({ ...form, amazon_url: v })} placeholder="https://www.amazon.in/dp/B0CCQMQ5JR" testid="form-url" />
          <Field label="Title (optional, recommended)" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Mimosa Red Banarasi Silk Saree" testid="form-title" />
          <Field label="Image URL (recommended)" type="url" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} placeholder="https://m.media-amazon.com/images/I/..." testid="form-image" />
          <Field label="Price (e.g., ₹3,499)" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="form-price" />
          <Field label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} testid="form-brand" />
          <Field label="Tags (comma-separated)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="saree, silk, red, bridal" testid="form-tags" />
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-3">Occasion *</label>
            <select
              value={form.occasion}
              onChange={(e) => setForm({ ...form, occasion: e.target.value })}
              data-testid="form-occasion"
              className="w-full bg-[#1A1820] border border-white/10 rounded-sm py-3 px-4 text-[#F7F5F2] focus:border-[#E3C2B0] outline-none"
            >
              {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-3">Gender *</label>
            <select
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              data-testid="form-gender"
              className="w-full bg-[#1A1820] border border-white/10 rounded-sm py-3 px-4 text-[#F7F5F2] focus:border-[#E3C2B0] outline-none"
            >
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="form-submit"
            className="md:col-span-2 mt-4 bg-[#E3C2B0] text-[#0A0908] py-4 rounded-sm hover:bg-[#F0D4C5] transition-colors text-xs uppercase tracking-[0.3em] disabled:opacity-50 flex items-center justify-center gap-3"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Saving</> : <><Plus className="w-4 h-4" strokeWidth={1.5} /> Add product</>}
          </button>
        </form>

        {/* List */}
        <div className="mt-16 flex items-center justify-between">
          <h2 className="font-serif text-3xl font-light">Catalog ({items.length})</h2>
          {loadingList && <Loader2 className="w-4 h-4 animate-spin text-[#A8A6A1]" strokeWidth={1.5} />}
        </div>
        {items.length === 0 ? (
          <p className="mt-6 text-[#A8A6A1] text-sm">No products yet. Add your first above.</p>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-list">
            {items.map((p) => (
              <div key={p.id} className="border border-white/10 rounded-sm overflow-hidden bg-[#1A1820] group" data-testid={`catalog-item-${p.id}`}>
                <div className="aspect-[3/4] bg-black/30 relative overflow-hidden">
                  <img src={p.image_url} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="text-xs uppercase tracking-[0.3em] text-[#E3C2B0]">{p.occasion} · {p.gender}</div>
                  <div className="mt-2 text-sm text-[#F7F5F2] line-clamp-2 leading-tight">{p.title}</div>
                  <div className="mt-1 text-xs text-[#A8A6A1]">{p.brand} {p.price ? `· ${p.price}` : ""}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(p.tags || []).slice(0, 4).map((t) => <span key={t} className="text-[10px] uppercase tracking-widest text-[#A8A6A1] border border-white/10 rounded px-2 py-0.5">{t}</span>)}
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <a href={p.amazon_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.3em] text-[#E3C2B0] inline-flex items-center gap-1 hover:text-[#F0D4C5]">
                      Open <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                    </a>
                    <button onClick={() => remove(p.id)} data-testid={`catalog-remove-${p.id}`} className="text-[10px] uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-red-300 inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, placeholder, type = "text", required = false, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-3">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] transition-colors"
    />
  </div>
);
